"use client"
import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { iconMap } from "@/app/(site)/_components/SanityGroups/HeadingGroup/helpers/iconMap"
import {
    FOCUS_VISIBLE_CLASS,
    FOCUS_VISIBLE_INSET_CLASS
} from "@/src/utils/a11y/focusVisible"
import { clsx } from "@/src/utils/clsx"
import { getGCGeneralLinkHref } from "@/src/utils/links/getGCGeneralLinkHref"
import { type ButtonLinkElementType } from "@/types/gc-modules-types"

const themeStyles: Partial<Record<ButtonLinkElementType["theme"], string>> = {
    "primary-black": "bg-main-black text-main-white hover:bg-strong active:bg-strong",
    "secondary-black":
        "shadow-[inset_0_0_0_1px_theme(colors.main-black)] text-main-black bg-transparent hover:shadow-[inset_0_0_0_1px_theme(colors.border-neutral)] hover:text-secondary-text active:text-secondary-text",
    "secondary-white":
        "shadow-[inset_0_0_0_1px_theme(colors.main-white)] text-main-white bg-transparent hover:shadow-[inset_0_0_0_1px_theme(colors.border-neutral)] hover:text-main-light-grey-disabled",
    "tertiary-black":
        "bg-transparent text-black-main hover:text-main-light-grey-disabled active:text-main-light-grey-disabled",
    "tertiary-white": "bg-transparent text-white hover:text-main-light-grey-disabled"
}

type ButtonKey =
    `${ButtonLinkElementType["theme"]}-${ButtonLinkElementType["size"]["desktop"]}`

const themeStylesPrimaryWhite: Partial<Record<ButtonKey, string>> = {
    "primary-white-small":
        "bg-main-white text-main-black hover:bg-muted active:bg-subtle",
    "primary-white-large":
        "bg-main-white text-main-black hover:bg-subtle active:bg-subtle"
}

const disabledStyles: Record<ButtonLinkElementType["theme"], string> = {
    "primary-black": "bg-main-medium-grey opacity-40 text-main-light-grey-disabled",
    "primary-white": "bg-main-medium-grey opacity-40 text-main-light-grey-disabled",
    "secondary-black":
        "shadow-[inset_0_0_0_1px_theme(colors.main-light-grey-disabled)] opacity-40 text-main-light-grey-disabled",
    "secondary-white":
        "shadow-[inset_0_0_0_1px_theme(colors.main-light-grey-disabled)] opacity-40 text-main-light-grey-disabled",
    "tertiary-black": "opacity-40",
    "tertiary-white": "opacity-40"
}

export default function ButtonLinkElement({
    text,
    size,
    theme,
    icon,
    iconPosition,
    link,
    disabled,
    className,
    prefetch = false
}: ButtonLinkElementType & {
    className?: string
    prefetch?: boolean
}) {
    const isSmallDesktop = size.desktop === "small"

    const isPrimaryWhite = theme === "primary-white"

    const buttonClass = clsx(
        "inline-flex h-40 w-max shrink-0 items-center gap-x-8 whitespace-nowrap rounded-full pt-18 pb-16",
        "transition-all duration-250 ease-out",
        isPrimaryWhite ? FOCUS_VISIBLE_INSET_CLASS : FOCUS_VISIBLE_CLASS,
        theme.startsWith("tertiary") ? "pl-0 pr-20" : "px-20",
        disabled
            ? disabledStyles[theme]
            : isPrimaryWhite
              ? themeStylesPrimaryWhite[`${theme}-${size.desktop}`]
              : themeStyles[theme],
        disabled ? "pointer-events-none" : "cursor-pointer",
        isSmallDesktop
            ? "font_trimMonoPoc_Regular_14_120_uppercase"
            : "font_trimMonoPoc_Regular_14_120_uppercase md:font_trimMonoPoc_Regular_15_130_uppercase",
        className
    )

    const IconComponent = icon ? iconMap[icon] : null

    if (!link) return null

    return link.external ? (
        <a
            href={getGCGeneralLinkHref(link)}
            className={buttonClass}
            aria-disabled={disabled}
            target="_blank"
            rel="noopener noreferrer"
        >
            {iconPosition === "leading" && IconComponent && (
                <IconComponent className="h-16 w-16" />
            )}
            <span>{text}</span>
            {iconPosition === "trailing" && IconComponent && (
                <IconComponent className="h-16 w-16" />
            )}
        </a>
    ) : (
        <DynamicLink
            prefetch={prefetch}
            href={getGCGeneralLinkHref(link) ?? ""}
            className={buttonClass}
            aria-disabled={disabled}
        >
            {iconPosition === "leading" && IconComponent && (
                <IconComponent className="h-16 w-16" />
            )}
            <span>{text}</span>
            {iconPosition === "trailing" && IconComponent && (
                <IconComponent className="h-16 w-16" />
            )}
        </DynamicLink>
    )
}
